import { fetchTable } from '../utils/fetchTable';
import { create } from 'zustand';
import { getRealEstateCommission, toNumber } from '../utils/calculations';
import { subscribeWithSelector } from 'zustand/middleware';

interface IRecords {
	id: string;
	fields: {
		sales_price: number;
		title_insurance_rate: number;
	};
}
interface IStore {
	//store variables
	firstName: string;
	lastName: string;
	email: string;
	homeAddress: string;
	realEstateCommission: { formatted: string; raw: number };
	realEstateCommissionPercent: string;
	salesPrice: { formatted: string; raw: number };
	reconveyanceFee: string;
	estimatedNetProceeds: number;
	titleInsurance: { formatted: string; raw: number };
	escrowFee: { formatted: string; raw: number };
	loanPayoffs: { formatted: string; raw: number };
	estimatedTotalCosts: any;
	sellersContributions: { formatted: string; raw: number };
	other1Label: string;
	other1: { formatted: string; raw: number };
	other2Label: string;
	other2: { formatted: string; raw: number };
	tableReference: Array<IRecords>;
	modalVisibility: boolean;
	modalMessage: { header: string; body: string };
	loader: boolean;

	// Action functions
	setFirstName: (input: string) => void;
	setLastName: (input: string) => void;
	setEmail: (input: string) => void;
	setHomeAddress: (input: string) => void;
	setRealEstateCommissionByAmount: (input: { value: string }) => void;
	setRealEstateCommissionByPercent: (input: string) => void;
	updateCommissionValues: (amount: number, percent: string) => void;
	setSalesPrice: (input: { formatted: string; raw: number }) => void;
	setEstimatedNetProceeds: () => void;
	setTitleInsurance: (input: { formatted: string; raw: number }) => void;
	setLoanPayoffs: (input: { formatted: string; raw: number }) => void;
	setSellersContributions: (input: { formatted: string; raw: number }) => void;
	setEscrowFee: (input: { formatted: string; raw: number }) => void;
	setOther1Label: (input: string) => void;
	setOther1: (input: { formatted: string; raw: number }) => void;
	setOther2Label: (input: string) => void;
	setOther2: (input: { formatted: string; raw: number }) => void;
	setTableReference: () => void;
	setEstimatedTotalCosts: () => void;
	resetState: () => void;
	setLoader: () => void;
	setModalVisibility: (bool: boolean) => void;
	setModalMessage: (header: string, body: string) => void;
}

const defaultState = {
	firstName: '',
	lastName: '',
	email: '',
	homeAddress: '',
	realEstateCommission: { formatted: '', raw: 0 },
	realEstateCommissionPercent: '',
	salesPrice: { formatted: '', raw: 0 },
	estimatedNetProceeds: 0,
	titleInsurance: { formatted: '', raw: 0 },
	escrowFee: { formatted: '', raw: 0 },
	loanPayoffs: { formatted: '', raw: 0 },
	estimatedTotalCosts: 0,
	sellersContributions: { formatted: '', raw: 0 },
	reconveyanceFee: '',
	other1Label: '',
	other1: { formatted: '', raw: 0 },
	other2Label: '',
	other2: { formatted: '', raw: 0 },
};

const formatNumberState = (input: any) => {
	let value = input.value.replace(/[^0-9.]/g, '');
	return {
		formatted: Number(value).toLocaleString(),
		raw: Number(value),
	};
};

const calculateCommissionAmount = (price: number, percent: string): number => {
	const percentValue = parseFloat(percent) || 0;
	return (price * percentValue) / 100;
};

const calculateCommissionPercent = (amount: number, price: number): string => {
	if (price === 0) return '0';
	return ((amount / price) * 100).toFixed(2);
};

const formatDisplayState = (input: any) => {
	const display = new Intl.NumberFormat('en-US', {
		currency: 'USD',
		maximumFractionDigits: 0,
	}).format(input);

	return { formatted: display, raw: toNumber(display) };
};

export const useStore = create(
	subscribeWithSelector<IStore>((set, get) => ({
		firstName: '',
		lastName: '',
		email: '',
		homeAddress: '',
		realEstateCommission: { formatted: '', raw: 0 },
		realEstateCommissionPercent: '',
		salesPrice: { formatted: '', raw: 0 },
		estimatedNetProceeds: 0,
		titleInsurance: { formatted: '', raw: 0 },
		escrowFee: { formatted: '', raw: 0 },
		loanPayoffs: { formatted: '', raw: 0 },
		estimatedTotalCosts: '',
		sellersContributions: { formatted: '', raw: 0 },
		reconveyanceFee: '',
		other1Label: '',
		other1: { formatted: '', raw: 0 },
		other2Label: '',
		other2: { formatted: '', raw: 0 },
		tableReference: [],
		modalVisibility: false,
		loader: false,
		modalMessage: { header: '', body: '' },

		// Action functions
		setFirstName: (input) => set(() => ({ firstName: input })),

		setLastName: (input) => set(() => ({ lastName: input })),

		setEmail: (input) => set(() => ({ email: input })),

		setHomeAddress: (input) => set(() => ({ homeAddress: input })),

		setRealEstateCommissionPercent: (input: any) => {
			set(() => ({ realEstateCommissionPercent: input }));
			get().setEstimatedNetProceeds();
			get().setEstimatedTotalCosts();
		},
		setRealEstateCommissionByAmount: (input) => {
			const formattedState = formatNumberState(input);
			const newPercent = calculateCommissionPercent(
				formattedState.raw,
				get().salesPrice.raw
			);

			get().updateCommissionValues(formattedState.raw, newPercent);
		},
		setRealEstateCommissionByPercent: (input) => {
			// Remove any non-numeric characters except decimal point
			const cleanPercent = input.replace(/[^0-9.]/g, '');
			const newAmount = calculateCommissionAmount(
				get().salesPrice.raw,
				cleanPercent
			);

			get().updateCommissionValues(newAmount, cleanPercent);
		},

		updateCommissionValues: (amount: number, percent: string) => {
			set(() => ({
				realEstateCommission: {
					formatted: amount.toLocaleString(),
					raw: amount,
				},
				realEstateCommissionPercent: percent,
			}));

			// Update dependent calculations
			get().setEstimatedTotalCosts();
			get().setEstimatedNetProceeds();
		},

		setSalesPrice: (input) => {
			const formattedPrice = formatNumberState(input);
			set(() => ({ salesPrice: formattedPrice }));

			// Recalculate commission based on new price while maintaining the same percentage
			const newAmount = calculateCommissionAmount(
				formattedPrice.raw,
				get().realEstateCommissionPercent
			);

			get().updateCommissionValues(
				newAmount,
				get().realEstateCommissionPercent
			);
			get().setTitleInsurance(input);
		},

		setTitleInsurance: (input) => {
			set(() => ({ titleInsurance: formatDisplayState(input) }));
			get().setEstimatedTotalCosts();
			get().setEstimatedNetProceeds();
		},

		setLoanPayoffs: (input) => {
			set(() => ({ loanPayoffs: formatNumberState(input) }));
			get().setEstimatedTotalCosts();
			get().setEstimatedNetProceeds();
		},

		setSellersContributions: (input) =>
			set(() => ({ sellersContributions: formatNumberState(input) })),
		setOther1Label: (input) => set(() => ({ other1Label: input })),
		setOther1: (input) => set(() => ({ other1: formatNumberState(input) })),
		setOther2Label: (input) => set(() => ({ other2Label: input })),
		setOther2: (input) => set(() => ({ other2: formatNumberState(input) })),
		setReconveyanceFee: () => {
			if (get().loanPayoffs.raw === 0) {
				set({ reconveyanceFee: '0' });
			} else {
				set({ reconveyanceFee: '75' });
			}

			get().setEstimatedTotalCosts();
			get().setEstimatedNetProceeds();
		},
		setRealEstateCommission: (input: any) => {
			console.log({ commission: input });
			const commission = getRealEstateCommission(
				get().salesPrice.raw,
				get().realEstateCommissionPercent
			);

			set(() => ({
				realEstateCommission: formatNumberState(commission),
			}));
			get().setEstimatedTotalCosts();
			get().setEstimatedNetProceeds();
		},
		setEscrowFee: () =>
			set(() => {
				if (get().salesPrice.raw === 0) {
					return { escrowFee: { formatted: '', raw: 0 } };
				}
				let escrow_fee = (Math.ceil(get().salesPrice.raw / 1000) * 3 + 150) / 2;
				let escrow_fee_after_minimum = escrow_fee < 600 ? 600 : escrow_fee;
				return { escrowFee: formatDisplayState(escrow_fee_after_minimum) };
			}),
		setEstimatedNetProceeds: () => {
			const estimatedNetProceeds =
				get().salesPrice.raw - Number(get().estimatedTotalCosts);

			set({
				estimatedNetProceeds,
			});
		},
		setEstimatedTotalCosts: () => {
			const estimatedTotalCosts =
				get().other1.raw +
				get().other2.raw +
				get().sellersContributions.raw +
				Number(get().reconveyanceFee) +
				get().titleInsurance.raw +
				get().realEstateCommission.raw +
				get().loanPayoffs.raw +
				get().escrowFee.raw;
			set({
				estimatedTotalCosts,
			});
		},
		setTableReference: async () => {
			set({ loader: true });
			const response = await fetchTable();
			set({ tableReference: response?.body, loader: false });
		},
		resetState: () => set(defaultState),

		setLoader: () => set({ loader: !get().loader }),

		setModalMessage: (header, body) =>
			set(() => ({ modalMessage: { header, body } })),
		setModalVisibility: () =>
			set(() => ({ modalVisibility: !get().modalVisibility })),
	}))
);
